// Parks a hero prompt across the signup/login round-trip (and across tabs and
// OAuth redirects — hence localStorage, not sessionStorage). Written by prompt
// heroes (e.g. /explore), consumed once by the /create composer.
import { PROMPT_MAX_LENGTH } from "app/utils/prompt_length"

const STORAGE_KEY = "conversation:pendingPrompt"
const STALE_AFTER_MS = 30 * 60 * 1000

export type PendingPromptSource = "explore_prompt_hero" | "home_prompt_hero"
const SOURCES: readonly PendingPromptSource[] = ["explore_prompt_hero", "home_prompt_hero"]

export type PendingPromptEntry = {
  prompt: string
  source: PendingPromptSource
  createdAt: number
  // One-time hand-off token: the hero puts it in the ?autostart= param, and
  // /create only auto-sends when the param matches the stored entry. Writing
  // a new entry overwrites the token, so only the latest hand-off can
  // auto-start — older tabs still holding the flag fall back to pre-fill.
  token: string
}

const getStorage = (): Storage | null => {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export const sanitizePrompt = (prompt: string): string =>
  prompt
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .trim()
    .slice(0, PROMPT_MAX_LENGTH)

const parseEntry = (raw: string | null): PendingPromptEntry | null => {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== "object") return null
  const obj = parsed as Record<string, unknown>
  if (typeof obj.prompt !== "string") return null
  if (typeof obj.createdAt !== "number" || !Number.isFinite(obj.createdAt)) return null
  const prompt = sanitizePrompt(obj.prompt)
  if (prompt.length === 0) return null
  return {
    prompt,
    source: SOURCES.includes(obj.source as PendingPromptSource)
      ? (obj.source as PendingPromptSource)
      : "explore_prompt_hero",
    createdAt: obj.createdAt,
    // Entries written before tokens existed read as an empty token, which can
    // never match an ?autostart= param — they degrade to pre-fill only.
    token: typeof obj.token === "string" ? obj.token : "",
  }
}

const generateToken = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

// Returns the entry's one-time hand-off token, or null when nothing was
// stored (empty prompt, storage unavailable) — callers should then navigate
// without an autostart param.
export const savePendingPrompt = (prompt: string, source: PendingPromptSource): string | null => {
  const storage = getStorage()
  if (!storage) return null
  const sanitized = sanitizePrompt(prompt)
  if (sanitized.length === 0) return null
  const token = generateToken()
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ prompt: sanitized, source, createdAt: Date.now(), token }))
  } catch {
    return null
  }
  return token
}

// Removes the key unconditionally so an abandoned or expired prompt never
// fires later; returns the entry only while it is still fresh.
//
// Deliberately single-consumer, not atomic across browsing contexts: two
// /create tabs mounting in the same instant can both read the entry before
// either removes it. That race only duplicates a pre-filled intro that still
// needs an explicit send — auto-send additionally requires the entry's
// one-time token in the ?autostart= param, and writing an entry overwrites
// any previous token, so at most one live hand-off can match.
export const consumePendingPrompt = (): PendingPromptEntry | null => {
  const storage = getStorage()
  if (!storage) return null
  const entry = parseEntry(storage.getItem(STORAGE_KEY))
  try {
    storage.removeItem(STORAGE_KEY)
  } catch {
    // best-effort
  }
  if (!entry) return null
  const age = Date.now() - entry.createdAt
  return age >= 0 && age < STALE_AFTER_MS ? entry : null
}
